import axios from "axios";
import { useEffect, useRef, useState } from "react";
import { useMediaQuery } from "react-responsive";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { X_API_KEY } from "../../constants";

export const useUploadPolicyData = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const mediaStreamRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const mediaRecorderRef = useRef(null);

  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [videoSrc, setVideoSrc] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [videoRecInBase64, setVideoRecInBase64] = useState("");
  const [apbModel, setApbModel] = useState("");
  const [loading, setLoading] = useState(false);
  const [apbBrands, setApbBrands] = useState("");
  const [bppsTaxId, setBppsTaxId] = useState("");
  const [bppsPlanId, setBppsPlanId] = useState("");
  const [isStatus, setIsStatus] = useState("");
  const [bppsTaxIdList, setBppsTaxIdList] = useState([]);
  const [selectedValue, setSelectedValue] = useState("Select an option");
  const [videoPreviewStream, setVideoPreviewStream] = useState(null);
  const [isUserMobile, setIsUserMobile] = useState("");
  const [isUserName, setIsUserName] = useState("");
  const [isUserEmail, setIsUserEmail] = useState("");
  const [isUserImei, setIsUserImei] = useState("");
  const [videoUrl, setVideoUrl] = useState(null);

  const videoBlob = location.state?.videoBlob;
  const isXs = useMediaQuery({ maxWidth: 575 });
  const isMd = useMediaQuery({ minWidth: 768 });

  useEffect(() => {
    if (videoBlob) {
      const url = URL.createObjectURL(videoBlob);
      setVideoUrl(url);

      return () => URL.revokeObjectURL(url);
    }
  }, [videoBlob]);

  // Determine if the form has user-entered data or a recorded video (dirty state)
  const hasFormInput =
    Boolean(isUserMobile) ||
    Boolean(isUserName) ||
    Boolean(isUserEmail) ||
    Boolean(isUserImei);
  const hasRecordedVideo =
    Boolean(videoRecInBase64) || Boolean(videoUrl) || isRecording;
  const preventBack = hasFormInput || hasRecordedVideo;

  // Conditionally prevent navigation when the user has started filling the form or recorded a video
  useEffect(() => {
    if (!preventBack) return;

    const currentUrl = window.location.href;
    window.history.pushState(null, null, currentUrl);

    const handlePopState = () => {
      window.history.pushState(null, null, currentUrl);
    };

    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue =
        "Are you sure you want to leave? The screen damage test will be lost.";
      return "Are you sure you want to leave? The screen damage test will be lost.";
    };

    window.addEventListener("popstate", handlePopState);
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("popstate", handlePopState);
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [preventBack]);

  const handleSelect = (eventKey) => {
    setSelectedValue(eventKey);
  };

  const searchTByMobileApi = (searchQuery) => {
    axios(
      `https://stag.acrossassist.com/api/partner/v1/apb/GetBppsTaxIdByMobileNumber?CustomerMobileNumber=${searchQuery}`,
      {
        method: "GET",
        headers: { "x-api-key": X_API_KEY, "Content-Type": "application/json" },
      }
    )
      .then((res) => {
        if (res?.data?.statusCode === 200) {
          setBppsTaxIdList(res?.data?.data);
        }
        if (res?.data?.statusCode === 400) {
          toast.error(res?.data?.msg);
        }
        if (res?.data?.statusCode === 404) {
          setErrorMessage("*Customer's mobile number does not exist.");
          setShowError(true);
        }
      })
      .catch((e) => {});
  };

  const getBrandAndModelByBppsTaxIdApi = (refId) => {
    const DATA = { BBPSTaxRefId: refId };

    axios(
      "https://portal.acrossassist.com/api/partner/v1/apb/GetBrandAndModelByBppsTaxId",
      {
        method: "POST",
        data: DATA,
        headers: {
          "x-api-key": X_API_KEY,
        },
      }
    )
      .then((res) => {
        if (res?.data?.statusCode === 200) {
          setApbBrands(res?.data?.data?.brands);
          setApbModel(res?.data?.data?.model);
        }
        if (res?.data?.statusCode === 400) {
          toast.error(res?.data?.msg);
        }
        if (res?.data?.statusCode === 404) {
          toast.error(res?.data?.msg);
        }
      })
      .catch((e) => {});
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" },
      });

      mediaStreamRef.current = stream;

      setVideoPreviewStream(stream);

      mediaRecorderRef.current = new MediaRecorder(stream);
      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          recordedChunksRef.current.push(event.data);
        }
      };

      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(recordedChunksRef.current, { type: "video/mp4" });
        recordedChunksRef.current = [];

        const videoURL = URL.createObjectURL(blob);
        setVideoSrc(videoURL);
        convertToBase64(blob);
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
    } catch (error) {}
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
    }

    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
    }

    setVideoPreviewStream(null);
    setIsRecording(false);
  };

  const convertToBase64 = (blob) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result.split(",")[1];
      setVideoRecInBase64(base64String);
    };
    reader.readAsDataURL(blob);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const imeiRegex = /^[0-9]+$/;
    const nameRegex = /^[a-zA-Z .]{2,}$/;

    if (!isUserMobile) {
      setErrorMessage(
        "*Please enter your mobile number to search for the BBPS Tax ID."
      );
      setShowError(true);
    } else if (isUserMobile?.length < 10) {
      setErrorMessage("*Mobile number must be 10 digits.");
      setShowError(true);
    } else if (!isUserName) {
      setErrorMessage("*Please enter your name.");
      setShowError(true);
    } else if (!nameRegex.test(isUserName)) {
      setErrorMessage("*Name must contain only letters, spaces, or periods.");
      setShowError(true);
    } else if (!isUserImei) {
      setErrorMessage("*Please enter the IMEI number.");
      setShowError(true);
    } else if (isUserImei.length < 15) {
      setErrorMessage("*The IMEI number must be 15 digits long.");
      setShowError(true);
    } else if (!imeiRegex.test(isUserImei)) {
      setErrorMessage("*The IMEI number must contain only digits.");
      setShowError(true);
    } else if (videoRecInBase64 === "") {
      // setErrorMessage("*Please record a video and upload it.");
      // setShowError(true);
    } else {
      uploadDetailsApi();
      setShowError(false);
    }
  };

  const handleClose = () => {
    setShowError(false);
  };

  const uploadDetailsApi = async (values) => {
    setLoading(true);
    const DATA = {
      Email: isUserEmail,
      BrandName: apbBrands,
      ModelName: apbModel,
      UserName: isUserName,
      MobileNumber: isUserMobile,
      DeviceImage: videoRecInBase64,
      imeiOne: isUserImei,
      PaymentRequestId: bppsTaxId,
    };

    axios(
      "https://portal.acrossassist.com/api/partner/v1/apb/SaveAirtelPaymentBankDetailsVideo",
      {
        method: "POST",
        data: DATA,
        headers: {
          "x-api-key": X_API_KEY,
          "Content-Type": "application/json",
        },
      }
    )
      .then((res) => {
        if (res?.data?.statusCode === 200) {
          setVideoRecInBase64("");
          const data = {
            createdDate: res?.data?.createddate,
            phoneNumber: res?.data?.mobilenumber,
          };
          navigate("/status", { state: data });
          setLoading(false);
        } else {
          toast.error("Please validate your form and Try again !");
          setLoading(false);
        }
        setLoading(false);
      })
      .catch((e) => {
        toast.error("Form not submitted. Please try again");
        setLoading(false);
      });
  };

  return {
    preventBack,
    isXs,
    isMd,
    isUserMobile,
    setIsUserMobile,
    isUserName,
    setIsUserName,
    isUserEmail,
    setIsUserEmail,
    isUserImei,
    setIsUserImei,
    bppsTaxIdList,
    selectedValue,
    apbModel,
    apbBrands,
    setBppsTaxId,
    setBppsPlanId,
    setIsStatus,
    showError,
    setShowError,
    errorMessage,
    setErrorMessage,
    videoRecInBase64,
    videoUrl,
    loading,
    videoPreviewStream,
    isRecording,
    handleSubmit,
    searchTByMobileApi,
    handleSelect,
    getBrandAndModelByBppsTaxIdApi,
    handleClose,
    startRecording,
    stopRecording,
  };
};
